import { type RosterEntry } from "@/lib/types/roster"

/**
 * Creates navigation data for player modals from a list of roster entries
 */
export function createPlayerNavigationData(
    players: RosterEntry[],
    context?: string
): { playerIds: number[]; context?: string } {
    const playerIds = players
        .map(entry => Number(entry.player.id))
        .filter(id => !isNaN(id) && !id.toString().startsWith('empty-'))

    return {
        playerIds,
        context
    }
}

/**
 * Creates navigation data for a combined list of players from different sections
 * (e.g., starters, bench, injured)
 */
export function createCombinedPlayerNavigationData(
    playerSections: RosterEntry[][],
    context?: string
): { playerIds: number[]; context?: string } {
    const allPlayers = playerSections.flat()
    return createPlayerNavigationData(allPlayers, context)
}

/**
 * Hook to create navigation data for roster components
 */
export function usePlayerNavigation(
    hitters: RosterEntry[],
    pitchers: RosterEntry[],
    benchHitters: RosterEntry[] = [],
    benchPitchers: RosterEntry[] = [],
    injuredHitters: RosterEntry[] = [],
    injuredPitchers: RosterEntry[] = [],
    context: string = 'team-roster'
) {
    // Combine all hitters (starters, bench, injured)
    const allHitters = [...hitters, ...benchHitters, ...injuredHitters]
    const hitterNavigation = createPlayerNavigationData(allHitters, `${context}-hitters`)

    // Combine all pitchers (starters, bench, injured)
    const allPitchers = [...pitchers, ...benchPitchers, ...injuredPitchers]
    const pitcherNavigation = createPlayerNavigationData(allPitchers, `${context}-pitchers`)

    // Combined navigation for all players
    const allPlayersNavigation = createCombinedPlayerNavigationData(
        [allHitters, allPitchers],
        context
    )

    return {
        hitterNavigation,
        pitcherNavigation,
        allPlayersNavigation,
        getNavigationForPlayer: (playerId: number, playerType: 'hitter' | 'pitcher' | 'auto' = 'auto') => {
            if (playerType === 'auto') {
                // Determine player type by checking which array contains the player
                const isHitter = allHitters.some(entry => Number(entry.player.id) === playerId)
                return isHitter ? hitterNavigation : pitcherNavigation
            }
            return playerType === 'hitter' ? hitterNavigation : pitcherNavigation
        }
    }
} 